import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ApiMeta } from '../../../_core/_model/api-response';
import { UserNotificationDto } from '../../../_core/_model/additional-documents';
import { UserNotificationsService } from '../../../_core/_services/user-notifications.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-user-notifications',
  standalone: false,
  templateUrl: './user-notifications.html',
  styleUrl: './user-notifications.css'
})
export class UserNotifications implements OnInit {
  loading = false;
  markingAll = false;
  page = 1;
  filter: 'all' | 'unread' | 'read' = 'all';
  items: UserNotificationDto[] = [];
  meta: ApiMeta | null = null;
  error: string | null = null;
  actingId: string | null = null;

  constructor(
    private notifications: UserNotificationsService,
    private router: Router,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(page = 1): void {
    this.page = page;
    this.loading = true;
    this.error = null;

    const isRead = this.filter === 'all' ? null : this.filter === 'read';
    this.notifications.getNotifications(isRead, page)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => {
          this.items = response.data || [];
          this.meta = response.meta || null;
          this.notifications.refreshSummary();
        },
        error: error => {
          this.items = [];
          this.meta = null;
          this.error = error?.error?.message || 'Unable to load notifications.';
          this.notify.showError(this.error!, 'Notifications');
        }
      });
  }

  open(item: UserNotificationDto): void {
    if (this.actingId) return;
    this.actingId = item.id;

    const go = () => {
      this.actingId = null;
      if (item.actionUrl) this.router.navigateByUrl(item.actionUrl);
    };

    if (item.isRead) {
      go();
      return;
    }

    this.notifications.markRead(item.id).subscribe({
      next: () => {
        item.isRead = true;
        item.readAt = new Date().toISOString();
        go();
      },
      error: () => {
        this.actingId = null;
        this.notify.showError('Unable to mark the notification as read.', 'Notifications');
      }
    });
  }

  markAllRead(): void {
    if (this.markingAll) return;
    this.markingAll = true;

    this.notifications.markAllRead()
      .pipe(finalize(() => (this.markingAll = false)))
      .subscribe({
        next: response => {
          this.items = this.items.map(item => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() }));
          this.notify.showSuccess(response.message || 'All notifications marked as read.', 'Notifications');
          if (this.filter === 'unread') this.load(1);
        },
        error: error => this.notify.showError(error?.error?.message || 'Unable to update notifications.', 'Notifications')
      });
  }

  typeIcon(type: string): string {
    switch (type) {
      case 'DepositDocumentApproved': return '✓';
      case 'DepositDocumentRejected': return '!';
      case 'DepositDocumentSubmitted': return '↑';
      default: return '📄';
    }
  }

  typeClass(type: string): string {
    switch (type) {
      case 'DepositDocumentApproved': return 'bg-success-subtle text-success';
      case 'DepositDocumentRejected': return 'bg-danger-subtle text-danger';
      case 'DepositDocumentSubmitted': return 'bg-info-subtle text-info';
      default: return 'bg-warning-subtle text-warning';
    }
  }
}
